import {useId} from 'react';
import {cn} from '@chekinapp/ui';
import {getDocumentTypeIcon} from './getDocumentTypeIcon';

type TileOption = {
  value: string;
  label: string;
};

type Props = {
  name: string;
  labelledBy?: string;
  options: TileOption[];
  value: string | null;
  onChange: (value: string) => void;
  error?: string;
};

function DocumentTypeTiles({name, labelledBy, options, value, onChange, error}: Props) {
  const errorId = useId();

  return (
    <div className="flex flex-col gap-2">
      <div
        role="radiogroup"
        aria-labelledby={labelledBy}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-2.5"
      >
        {options.map(option => {
          const isSelected = option.value === value;
          return (
            <label
              key={option.value}
              className={cn(
                'document-type-tile flex min-h-[52px] cursor-pointer items-center gap-2.5 rounded-[14px] border-2 px-3 py-2 transition-colors',
                'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[var(--chekin-color-brand-blue)] has-[:focus-visible]:ring-offset-2',
                isSelected
                  ? 'border-[color:var(--radio-cards-group-selected-border)] bg-[var(--chekin-blue-tint-info)] text-[var(--chekin-color-brand-blue)]'
                  : 'border-[color:var(--radio-cards-group-border)] bg-[var(--radio-cards-group-bg)] text-[var(--form-title-color,#222222)] hover:border-[color:var(--radio-cards-group-hover-border)]',
                error && 'border-[color:var(--radio-cards-group-invalid-border)]',
              )}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={isSelected}
                onChange={() => onChange(option.value)}
                className="sr-only"
              />
              <span
                aria-hidden
                className="flex size-8 flex-none items-center justify-center [&>svg]:size-6 [&>svg]:stroke-[1.75]"
              >
                {getDocumentTypeIcon(option.value)}
              </span>
              <span className="min-w-0 break-words text-[14px] font-semibold leading-[18px]">
                {option.label}
              </span>
            </label>
          );
        })}
      </div>
      {error && (
        <p
          id={errorId}
          role="alert"
          className="m-0 text-[13px] text-[var(--chekin-color-brand-red)]"
        >
          {error}
        </p>
      )}
    </div>
  );
}

export {DocumentTypeTiles};
